// frontend/src/Mystery250.js
// Mystery250 – NextScenes-styled workspace (List + Details + Editor)
// Build: 2026-01-07-mystery250-difficulty-v1 + Cover Image URL (2026-01-08) + Video Trailer Embed (2026-01-08)
// Patch: 2026-01-08 – Trailer save is not blocked + fallback "Open trailer" link
// Patch: 2026-04-28 – Compact vault layout, search/filter panel, bounded media, shelf arrows, editor access restored
// Polish: 2026-05-16 – Weekly mystery feature, reader-attempt signals, sectional puzzle reading
// Build: 2026-05-17-mystery250-media-upload-v1

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./App.css";
import "./Mystery250.css";
import { useUser } from "./UserContext";
import { api } from "./api";

const DIFFICULTIES = ["EASY", "MEDIUM", "HARD", "EXPERT"];

// Default cover images per difficulty (local files in /public OR any absolute URLs).
// If you prefer local images, place them in frontend/public/mystery-covers/...
const DEFAULT_COVERS = {
  EASY: "/mystery-covers/easy.jpg",
  MEDIUM: "/mystery-covers/medium.jpg",
  HARD: "/mystery-covers/hard.jpg",
  EXPERT: "/mystery-covers/expert.jpg",
};

const M250_TEXT = {
  en: {
    brand: "NextScenes",
    kicker: "Mystery250 feature",
    title: "Mystery250",
    subtitle:
      "Mystery250 is a short-mystery feature for sharp minds. Choose an answer, learn from the explanation, and grow your instincts.",
    mysterySingular: "mystery",
    mysteryPlural: "mysteries",
    toolsEnabled: "Writer/Admin tools enabled",
    viewerMode: "Viewer mode",
    refreshing: "Refreshing...",
    refreshList: "Refresh list",
    newMystery: "New mystery",
    loginToContribute: "Login to contribute",
    home: "HOME",
    note: "Fair puzzles sharpen the mind. Cruel puzzles merely show off.",
    weeklyKicker: "Mystery of the Week",
    weeklyTitle: "This week’s challenge",
    weeklyPrompt: "Read the setup, inspect the clues, and make your answer before the reveal.",
    tryMystery: "Try this mystery",
    activity: "Activity",
    attemptSingular: "attempt",
    attempts: "attempts",
    solved: "solved",
    successRate: "success rate",
    noAttemptsYet: "Waiting for first attempts",
    attemptedByYou: "Attempted by you",
    notAttemptedByYou: "Not attempted yet",
    communityPulse: "Reader pulse",
    mysteryStory: "The mystery",
    clues: "Clues",
    clueFallback: "The clues are hidden in the setup.",
    chooseAnswer: "Choose your answer",
    reveal: "The reveal",
    whyTrue: "Why this is true",
    educativeNote: "What this mystery teaches",
    learningFallback: "Good mysteries train observation, patience, and fair reasoning.",
    yourAttemptRecorded: "Your attempt has been recorded on this device.",
    mostReaders: "Most readers chose",
    filtersAria: "Mystery filters",
    searchFilters: "Search & Filters",
    filterSub: "Find a mystery by title, clue, option, or difficulty.",
    showing: "Showing",
    of: "of",
    search: "Search",
    searchPlaceholder: "Search title, clue, answer, difficulty...",
    difficulty: "Difficulty",
    allDifficulties: "All difficulties",
    clear: "Clear",
    mysteries: "Mysteries",
    pickOne: "Pick one to view or play.",
    scrollLeft: "Scroll mysteries left",
    scrollRight: "Scroll mysteries right",
    newShort: "New",
    loadingMysteries: "Loading mysteries…",
    noMysteries: "No mysteries yet.",
    noMatch: "No mysteries match this search.",
    untitled: "Untitled",
    mysteryCover: "Mystery cover",
    details: "Details",
    readPuzzle: "Read the puzzle and choices.",
    selectMystery: "Select a mystery from the left.",
    edit: "Edit",
    chooseMystery: "Choose a mystery to view it here.",
    trailer: "Trailer",
    trailerLink: "Trailer link:",
    openTrailer: "Open trailer",
    reset: "Reset",
    submitAnswer: "Submit answer",
    recordingAttempt: "Recording...",
    attemptSyncFailed: "Your answer was recorded on this device, but it could not be synced online.",
    alreadyAttempted: "You have already attempted this mystery.",
    answerRecorded: "Your answer has been recorded.",
    revealPending: "The answer will be revealed later.",
    revealOpensOn: "Reveal opens on",
    revealSchedule: "Reveal Date/Time (optional)",
    revealSchedulePlaceholder: "YYYY-MM-DDTHH:mm or leave blank",
    revealScheduleTip:
      "Set a future date/time to collect attempts before showing the answer. Leave blank to keep the answer hidden until a reveal date is set.",
    correct: "Correct. Your instincts are awake.",
    answer: "Answer",
    notQuite: "Not quite. Try again, or read the explanation.",
    youPicked: "You picked",
    correctOption: "Correct option",
    explanation: "Explanation",
    noExplanation: "No explanation provided.",
    submitReveal: "Submit to record your answer. The reveal opens only when scheduled.",
    submitLocked: "Your attempt is already recorded.",
    editMystery: "Edit Mystery",
    createMystery: "Create Mystery",
    updateCarefully: "Update the puzzle carefully.",
    writeFair: "Write a clear puzzle and fair options.",
    cancel: "Cancel",
    loginWriterAdmin: "Log in as a writer or admin to create mysteries.",
    titleLabel: "Title",
    titlePlaceholder: "e.g., The Missing Ring",
    coverUrl: "Cover Image URL (optional)",
    coverPlaceholder: "https://...jpg / png / webp",
    coverTip:
      "Tip: use a direct image link. If this field is empty, the default cover changes automatically when you save a new difficulty.",
    trailerUrl: "Trailer URL (YouTube/Vimeo, optional)",
    trailerPlaceholder: "https://youtube.com/watch?v=... or https://vimeo.com/...",
    trailerTip:
      "Tip: paste a normal YouTube/Vimeo link. If it’s not embeddable, it will still save and show as a link.",
    coverUploadLabel: "Select / Upload Image",
    coverUploadHint: "JPG, PNG, or WEBP. Maximum 8 MB. The image should create atmosphere, not reveal the answer.",
    coverUploadButton: "Upload selected image",
    coverUploading: "Uploading image...",
    coverUploadSuccess: "Image uploaded. The cover URL has been added.",
    coverUploadFailed: "Image upload failed.",
    videoUploadLabel: "Select / Upload Video",
    videoUploadHint: "Optional MP4, MOV, WEBM, or M4V teaser. Maximum 80 MB.",
    videoUploadButton: "Upload selected video",
    videoUploading: "Uploading video...",
    videoUploadSuccess: "Video uploaded. The trailer URL has been added.",
    videoUploadFailed: "Video upload failed.",
    selectedFile: "Selected file",
    question: "Question",
    questionPlaceholder: "Write the mystery question...",
    optionA: "Option A",
    optionB: "Option B",
    optionC: "Option C",
    optionD: "Option D",
    explanationOptional: "Explanation (optional)",
    explanationPlaceholder: "Explain why the answer is correct...",
    saving: "Saving...",
    saveChanges: "Save Changes",
    createMysteryButton: "Create Mystery",
    footnote:
      "Choose the difficulty like a responsible elder: don’t hand a toddler a riddle meant for philosophers.",
    changesSaved: "Changes saved.",
    mysteryCreated: "Mystery created.",
    failedLoad: "Failed to load mysteries.",
    failedSaveChanges: "Failed to save changes.",
    failedCreate: "Failed to create mystery.",
    enterQuestion: "Please enter the mystery question.",
    twoOptions: "Please provide at least two options.",
    correctEmpty: "Correct option points to an empty option. Fill it in or change the correct option.",
    trailerMustHttp: "Trailer URL must start with http:// or https://.",
    loginCreateEdit: "Log in as writer/admin to create or edit a Mystery250 question.",
    noEditSelected: "No mystery selected to edit.",
    ownOnly: "You can only edit your own puzzles. Admin can edit any puzzle.",
  },
  fr: {
    brand: "NextScenes",
    kicker: "Fonction Mystery250",
    title: "Mystery250",
    subtitle:
      "Mystery250 est une fonction de mini-mystères pour esprits attentifs. Choisissez une réponse, lisez l’explication et affinez votre instinct.",
    mysterySingular: "mystère",
    mysteryPlural: "mystères",
    toolsEnabled: "Outils auteur/admin activés",
    viewerMode: "Mode lecteur",
    refreshing: "Actualisation...",
    refreshList: "Actualiser la liste",
    newMystery: "Nouveau mystère",
    loginToContribute: "Connectez-vous pour contribuer",
    home: "ACCUEIL",
    note: "Les bons mystères aiguisent l’esprit. Les mystères cruels ne font que se montrer.",
    weeklyKicker: "Mystère de la semaine",
    weeklyTitle: "Le défi de la semaine",
    weeklyPrompt: "Lisez la situation, observez les indices, puis choisissez votre réponse avant la révélation.",
    tryMystery: "Tenter ce mystère",
    activity: "Activité",
    attemptSingular: "tentative",
    attempts: "tentatives",
    solved: "résolus",
    successRate: "réussite",
    noAttemptsYet: "En attente des premières tentatives",
    attemptedByYou: "Tenté par vous",
    notAttemptedByYou: "Pas encore tenté",
    communityPulse: "Réaction des lecteurs",
    mysteryStory: "Le mystère",
    clues: "Indices",
    clueFallback: "Les indices sont cachés dans la situation.",
    chooseAnswer: "Choisissez votre réponse",
    reveal: "La révélation",
    whyTrue: "Pourquoi c’est vrai",
    educativeNote: "Ce que ce mystère enseigne",
    learningFallback: "Un bon mystère apprend à observer, patienter et raisonner avec justice.",
    yourAttemptRecorded: "Votre tentative a été enregistrée sur cet appareil.",
    mostReaders: "La plupart des lecteurs ont choisi",
    filtersAria: "Filtres Mystery250",
    searchFilters: "Recherche et filtres",
    filterSub: "Trouvez un mystère par titre, indice, option ou difficulté.",
    showing: "Affichage",
    of: "sur",
    search: "Recherche",
    searchPlaceholder: "Rechercher par titre, indice, réponse, difficulté...",
    difficulty: "Difficulté",
    allDifficulties: "Toutes les difficultés",
    clear: "Effacer",
    mysteries: "Mystères",
    pickOne: "Choisissez-en un pour le voir ou jouer.",
    scrollLeft: "Faire défiler les mystères vers la gauche",
    scrollRight: "Faire défiler les mystères vers la droite",
    newShort: "Nouveau",
    loadingMysteries: "Chargement des mystères…",
    noMysteries: "Aucun mystère pour le moment.",
    noMatch: "Aucun mystère ne correspond à cette recherche.",
    untitled: "Sans titre",
    mysteryCover: "Couverture du mystère",
    details: "Détails",
    readPuzzle: "Lisez l’énigme et les choix.",
    selectMystery: "Sélectionnez un mystère à gauche.",
    edit: "Modifier",
    chooseMystery: "Choisissez un mystère pour l’afficher ici.",
    trailer: "Bande-annonce",
    trailerLink: "Lien de la bande-annonce :",
    openTrailer: "Ouvrir la bande-annonce",
    reset: "Réinitialiser",
    submitAnswer: "Soumettre la réponse",
    recordingAttempt: "Enregistrement...",
    attemptSyncFailed: "Votre réponse est enregistrée sur cet appareil, mais elle n’a pas pu être synchronisée en ligne.",
    alreadyAttempted: "Vous avez déjà tenté ce mystère.",
    answerRecorded: "Votre réponse a été enregistrée.",
    revealPending: "La réponse sera révélée plus tard.",
    revealOpensOn: "La révélation ouvre le",
    revealSchedule: "Date/heure de révélation (facultatif)",
    revealSchedulePlaceholder: "AAAA-MM-JJTHH:mm ou laisser vide",
    revealScheduleTip:
      "Fixez une date/heure future pour recueillir les tentatives avant d’afficher la réponse. Laissez vide pour garder la réponse cachée jusqu’à ce qu’une date soit définie.",
    correct: "Correct. Votre instinct est bien éveillé.",
    answer: "Réponse",
    notQuite: "Pas tout à fait. Réessayez ou lisez l’explication.",
    youPicked: "Votre choix",
    correctOption: "Bonne option",
    explanation: "Explication",
    noExplanation: "Aucune explication fournie.",
    submitReveal: "Soumettez pour enregistrer votre réponse. La révélation s’ouvrira à la date prévue.",
    submitLocked: "Votre tentative est déjà enregistrée.",
    editMystery: "Modifier le mystère",
    createMystery: "Créer un mystère",
    updateCarefully: "Mettez l’énigme à jour avec soin.",
    writeFair: "Écrivez une énigme claire avec des options justes.",
    cancel: "Annuler",
    loginWriterAdmin: "Connectez-vous comme auteur ou admin pour créer des mystères.",
    titleLabel: "Titre",
    titlePlaceholder: "ex. : La bague disparue",
    coverUrl: "URL de l’image de couverture (facultatif)",
    coverPlaceholder: "https://...jpg / png / webp",
    coverTip:
      "Astuce : utilisez un lien direct vers l’image. Si ce champ est vide, la couverture par défaut changera automatiquement selon la difficulté.",
    trailerUrl: "URL de bande-annonce (YouTube/Vimeo, facultatif)",
    trailerPlaceholder: "https://youtube.com/watch?v=... ou https://vimeo.com/...",
    trailerTip:
      "Astuce : collez un lien YouTube/Vimeo normal. S’il n’est pas intégrable, il sera quand même enregistré et affiché comme lien.",
    coverUploadLabel: "Sélectionner / téléverser une image",
    coverUploadHint: "JPG, PNG ou WEBP. Maximum 8 Mo. L’image doit créer une ambiance, sans révéler la réponse.",
    coverUploadButton: "Téléverser l’image sélectionnée",
    coverUploading: "Téléversement de l’image...",
    coverUploadSuccess: "Image téléversée. L’URL de couverture a été ajoutée.",
    coverUploadFailed: "Échec du téléversement de l’image.",
    videoUploadLabel: "Sélectionner / téléverser une vidéo",
    videoUploadHint: "Teaser facultatif MP4, MOV, WEBM ou M4V. Maximum 80 Mo.",
    videoUploadButton: "Téléverser la vidéo sélectionnée",
    videoUploading: "Téléversement de la vidéo...",
    videoUploadSuccess: "Vidéo téléversée. L’URL de bande-annonce a été ajoutée.",
    videoUploadFailed: "Échec du téléversement de la vidéo.",
    selectedFile: "Fichier sélectionné",
    question: "Question",
    questionPlaceholder: "Écrivez la question du mystère...",
    optionA: "Option A",
    optionB: "Option B",
    optionC: "Option C",
    optionD: "Option D",
    explanationOptional: "Explication (facultatif)",
    explanationPlaceholder: "Expliquez pourquoi la réponse est correcte...",
    saving: "Enregistrement...",
    saveChanges: "Enregistrer les modifications",
    createMysteryButton: "Créer le mystère",
    footnote:
      "Choisissez la difficulté avec sagesse : ne donnez pas à un enfant une énigme destinée aux philosophes.",
    changesSaved: "Modifications enregistrées.",
    mysteryCreated: "Mystère créé.",
    failedLoad: "Impossible de charger les mystères.",
    failedSaveChanges: "Échec de l’enregistrement des modifications.",
    failedCreate: "Échec de la création du mystère.",
    enterQuestion: "Veuillez saisir la question du mystère.",
    twoOptions: "Veuillez fournir au moins deux options.",
    correctEmpty:
      "La bonne option pointe vers une option vide. Remplissez-la ou changez la bonne option.",
    trailerMustHttp: "L’URL de bande-annonce doit commencer par http:// ou https://",
    loginCreateEdit:
      "Connectez-vous comme auteur/admin pour créer ou modifier une question Mystery250.",
    noEditSelected: "Aucun mystère sélectionné pour modification.",
    ownOnly: "Vous ne pouvez modifier que vos propres énigmes. L’admin peut modifier toutes les énigmes.",
  },
};

function readLang() {
  try {
    const value = String(window.localStorage.getItem("ns.lang") || "").trim().toLowerCase();
    return value === "fr" ? "fr" : "en";
  } catch {
    return "en";
  }
}

function safeStr(v) {
  return String(v || "");
}

function clampStr(v, max) {
  const s = safeStr(v).trim();
  if (!max) return s;
  return s.length > max ? s.slice(0, max) + "…" : s;
}

function getId(x) {
  return x?._id || x?.id || null;
}

function safeId(v) {
  const s = String(v || "").trim();
  return s ? s : "";
}

function safeDifficulty(v) {
  const d = String(v || "").trim().toUpperCase();
  return DIFFICULTIES.includes(d) ? d : "EASY";
}

function emptyForm() {
  return {
    title: "",
    question: "",
    optionA: "",
    optionB: "",
    optionC: "",
    optionD: "",
    correctOption: "A",
    explanation: "",
    difficulty: "EASY",
    imageUrl: "",
    videoUrl: "",
    revealAt: "",
  };
}

function splitOptionsToForm(options) {
  const o = Array.isArray(options) ? options : [];
  return {
    optionA: safeStr(o[0] || ""),
    optionB: safeStr(o[1] || ""),
    optionC: safeStr(o[2] || ""),
    optionD: safeStr(o[3] || ""),
  };
}

function indexForLetter(l) {
  const x = String(l || "A").toUpperCase();
  if (x === "B") return 1;
  if (x === "C") return 2;
  if (x === "D") return 3;
  return 0;
}

function letterForIndex(i) {
  return ["A", "B", "C", "D"][i] || "A";
}

function sanitizeUrl(u) {
  const s = String(u || "").trim();
  if (!s) return "";
  if (!/^https?:\/\//i.test(s)) return "";
  return s;
}

function getTrailerEmbed(urlRaw) {
  const url = sanitizeUrl(urlRaw);
  if (!url) return null;

  try {
    const u = new URL(url);

    // youtu.be/VIDEOID
    if (u.hostname.includes("youtu.be")) {
      const id = u.pathname.replace("/", "").trim();
      if (!id) return null;
      return { type: "youtube", embedSrc: `https://www.youtube.com/embed/${encodeURIComponent(id)}` };
    }

    // youtube.com
    if (u.hostname.includes("youtube.com")) {
      const v = u.searchParams.get("v");
      if (v) return { type: "youtube", embedSrc: `https://www.youtube.com/embed/${encodeURIComponent(v)}` };

      if (u.pathname.startsWith("/shorts/")) {
        const id = u.pathname.split("/shorts/")[1]?.split("/")[0]?.trim();
        if (id) return { type: "youtube", embedSrc: `https://www.youtube.com/embed/${encodeURIComponent(id)}` };
      }

      if (u.pathname.startsWith("/embed/")) {
        const id = u.pathname.split("/embed/")[1]?.split("/")[0]?.trim();
        if (id) return { type: "youtube", embedSrc: `https://www.youtube.com/embed/${encodeURIComponent(id)}` };
      }
    }

    // Direct uploaded video files (for Mystery250 Supabase uploads)
    if (/\.(mp4|webm|mov|m4v)(\?.*)?$/i.test(u.pathname)) {
      return { type: "direct", src: url };
    }

    // Vimeo
    if (u.hostname.includes("vimeo.com")) {
      const parts = u.pathname.split("/").filter(Boolean);
      const last = parts[parts.length - 1] || "";
      const id = /^\d+$/.test(last) ? last : "";

      if (id) return { type: "vimeo", embedSrc: `https://player.vimeo.com/video/${encodeURIComponent(id)}` };

      const idx = parts.indexOf("video");
      if (idx >= 0 && parts[idx + 1] && /^\d+$/.test(parts[idx + 1])) {
        const vid = parts[idx + 1];
        return { type: "vimeo", embedSrc: `https://player.vimeo.com/video/${encodeURIComponent(vid)}` };
      }
    }
  } catch {
    return null;
  }

  return null;
}

function pickCoverUrl(mystery) {
  const diff = safeDifficulty(mystery?.difficulty);
  const explicit = safeStr(mystery?.imageUrl).trim();
  return explicit || DEFAULT_COVERS[diff] || DEFAULT_COVERS.EASY || "";
}

function metricNumber(mystery, keys) {
  const obj = mystery && typeof mystery === "object" ? mystery : {};
  for (const key of keys) {
    const value = obj[key];
    const n = Number(value);
    if (Number.isFinite(n) && n >= 0) return Math.floor(n);
  }
  return null;
}

function metricString(mystery, keys) {
  const obj = mystery && typeof mystery === "object" ? mystery : {};
  for (const key of keys) {
    const value = safeStr(obj[key]).trim();
    if (value) return value;
  }
  return "";
}


function attemptLabel(count, ui) {
  const n = Number(count);
  if (Number.isFinite(n) && Math.floor(n) === 1) {
    return ui.attemptSingular || ui.attempts || "attempt";
  }
  return ui.attempts || "attempts";
}

function getMysteryStats(mystery) {
  const attempts = metricNumber(mystery, [
    "attemptCount",
    "attempts",
    "totalAttempts",
    "readerAttempts",
    "answerCount",
    "responsesCount",
  ]);

  const solved = metricNumber(mystery, [
    "solvedCount",
    "correctCount",
    "correctAnswers",
    "rightCount",
    "successfulAttempts",
  ]);

  const mostPicked = metricString(mystery, [
    "mostPicked",
    "mostChosen",
    "popularChoice",
    "topChoice",
  ]);

  const safeAttempts = attempts == null ? null : Math.max(0, attempts);
  const safeSolved =
    solved == null || safeAttempts == null ? null : Math.max(0, Math.min(safeAttempts, solved));

  const successRate =
    safeAttempts && safeSolved != null ? Math.round((safeSolved / safeAttempts) * 100) : null;

  return {
    attempts: safeAttempts,
    solved: safeSolved,
    successRate,
    mostPicked,
  };
}

function mysteryTimeScore(mystery) {
  const candidates = [
    mystery?.updatedAt,
    mystery?.createdAt,
    mystery?.publishedAt,
    mystery?.date,
  ];
  let best = 0;
  candidates.forEach((value) => {
    const t = new Date(value || 0).getTime();
    if (Number.isFinite(t) && t > best) best = t;
  });
  return best;
}

function splitMysteryBody(rawQuestion) {
  const raw = safeStr(rawQuestion);
  const lines = raw
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (!lines.length) {
    return { story: "", clues: [], finalQuestion: "" };
  }

  const clues = [];
  const storyLines = [];
  const questionLines = [];
  let inQuestion = false;

  lines.forEach((line) => {
    if (/^clue\s*\d+\s*:/i.test(line)) {
      clues.push(line);
      return;
    }

    if (/^question\s*:/i.test(line)) {
      inQuestion = true;
      const cleaned = line.replace(/^question\s*:\s*/i, "").trim();
      if (cleaned) questionLines.push(cleaned);
      return;
    }

    if (inQuestion) {
      questionLines.push(line);
      return;
    }

    storyLines.push(line);
  });

  return {
    story: storyLines.join("\n\n"),
    clues,
    finalQuestion: questionLines.join("\n\n"),
  };
}

function guessLearningNote(mystery, fallback) {
  const explicit = safeStr(
    mystery?.learningNote ||
      mystery?.educativeNote ||
      mystery?.lesson ||
      mystery?.teachingPoint ||
      ""
  ).trim();

  if (explicit) return explicit;

  const diff = safeDifficulty(mystery?.difficulty);
  if (diff === "EXPERT") {
    return "It teaches careful sequencing: when something happens without the expected trigger, the timeline matters.";
  }
  if (diff === "HARD") {
    return "It teaches readers to separate what people claim from what the evidence can prove.";
  }
  if (diff === "MEDIUM") {
    return "It teaches observation: small details can overturn a loud first impression.";
  }
  return fallback;
}


function slugifyMysteryRef(v) {
  return safeStr(v)
    .trim()
    .toLowerCase()
    .replace(/['’]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function readRequestedMysteryRef() {
  if (typeof window === "undefined") return "";
  try {
    const params = new URLSearchParams(window.location.search || "");
    return safeStr(params.get("mysteryId") || params.get("mystery") || "").trim();
  } catch {
    return "";
  }
}

function stripLeadingTheSlug(value) {
  return slugifyMysteryRef(value).replace(/^the-+/, "");
}

function matchesMysteryRef(mystery, rawRef) {
  const ref = safeStr(rawRef).trim();
  if (!ref || !mystery) return false;

  const refSlug = slugifyMysteryRef(ref);
  const refNoThe = stripLeadingTheSlug(ref);
  const ids = [
    getId(mystery),
    mystery?.slug,
    mystery?.title,
    mystery?.publicId,
    mystery?.externalId,
  ]
    .map((value) => safeStr(value).trim())
    .filter(Boolean);

  return ids.some((value) => {
    const valueSlug = slugifyMysteryRef(value);
    const valueNoThe = stripLeadingTheSlug(value);
    return (
      value === ref ||
      valueSlug === refSlug ||
      (!!refNoThe && !!valueNoThe && valueNoThe === refNoThe)
    );
  });
}

function findMysteryByRef(list, rawRef) {
  const rows = Array.isArray(list) ? list : [];
  if (!rawRef) return null;
  return rows.find((mystery) => matchesMysteryRef(mystery, rawRef)) || null;
}

function attemptStorageKey(mysteryId) {
  const id = safeId(mysteryId);
  return id ? `ns.m250.attempt.${id}` : "";
}

function readLocalAttempt(mysteryId) {
  const key = attemptStorageKey(mysteryId);
  if (!key || typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function writeLocalAttempt(mysteryId, payload) {
  const key = attemptStorageKey(mysteryId);
  if (!key || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify(payload || {}));
  } catch {}
}

function readOrCreateReaderKey() {
  if (typeof window === "undefined") return "";
  const storageKey = "ns.m250.readerKey";
  try {
    const existing = safeStr(window.localStorage.getItem(storageKey)).trim();
    if (existing) return existing;

    const generated =
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `guest_${Date.now()}_${Math.random().toString(16).slice(2)}`;

    window.localStorage.setItem(storageKey, generated);
    return generated;
  } catch {
    return "";
  }
}

function isMysteryRevealOpen(mystery) {
  if (!mystery) return false;
  if (mystery.revealOpen === true) return true;
  if (mystery.revealOpen === false) return false;

  const raw = mystery.revealAt;
  // No reveal date means the answer remains locked for public readers.
  if (!raw) return false;
  const t = new Date(raw).getTime();
  if (!Number.isFinite(t)) return false;
  return Date.now() >= t;
}

function formatRevealDate(value) {
  const raw = safeStr(value).trim();
  if (!raw) return "";
  const dt = new Date(raw);
  if (!Number.isFinite(dt.getTime())) return raw;
  try {
    return dt.toLocaleString();
  } catch {
    return raw;
  }
}

function toDatetimeLocalValue(value) {
  const raw = safeStr(value).trim();
  if (!raw) return "";
  const dt = new Date(raw);
  if (!Number.isFinite(dt.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(
    dt.getHours()
  )}:${pad(dt.getMinutes())}`;
}

function revealAtPayload(value) {
  const raw = safeStr(value).trim();
  if (!raw) return "";
  const dt = new Date(raw);
  if (!Number.isFinite(dt.getTime())) return raw;
  return dt.toISOString();
}

function isAlreadyAttemptMessage(value) {
  const s = safeStr(value).toLowerCase();
  return s.includes("already attempted") || s.includes("déjà tenté");
}


export default function Mystery250({ onNavigatePage, onNavigate }) {
  const { user } = useUser();

  const role = String(user?.role || "").toLowerCase();
  const isLoggedIn = !!user?.username;

  const isAdmin = isLoggedIn && role === "admin";
  const canCreate = isLoggedIn && (role === "admin" || role === "writer" || role === "moderator" || role === "power");

  const myUserId = safeId(user?.id || user?._id || "");

  const [mysteries, setMysteries] = useState([]);
  const [activeMysteryId, setActiveMysteryId] = useState(null);

  const [filterDifficulty, setFilterDifficulty] = useState("ALL");
  const [searchText, setSearchText] = useState("");

  const [mode, setMode] = useState("create"); // create | edit
  const [editingId, setEditingId] = useState(null);

  const [form, setForm] = useState(emptyForm());
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [selectedVideoFile, setSelectedVideoFile] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Play state
  const [picked, setPicked] = useState(null); // 0..3
  const [submitted, setSubmitted] = useState(false);
  const [attemptLog, setAttemptLog] = useState({});
  const [submittingAttempt, setSubmittingAttempt] = useState(false);

  const editorRef = useRef(null);
  const listRef = useRef(null);
  const readerKeyRef = useRef(readOrCreateReaderKey());

  const [lang, setLang] = useState(readLang());
  const ui = useMemo(() => M250_TEXT[lang] || M250_TEXT.en, [lang]);

  useEffect(() => {
    let alive = true;

    function syncLang() {
      if (!alive) return;
      setLang((prev) => {
        const next = readLang();
        return prev === next ? prev : next;
      });
    }

    const events = [
      "storage",
      "focus",
      "pageshow",
      "visibilitychange",
      "ns:lang",
      "ns-language-change",
      "languagechange",
    ];

    events.forEach((eventName) => window.addEventListener(eventName, syncLang));
    document.addEventListener("visibilitychange", syncLang);

    const timer = window.setInterval(syncLang, 650);

    syncLang();

    return () => {
      alive = false;
      events.forEach((eventName) => window.removeEventListener(eventName, syncLang));
      document.removeEventListener("visibilitychange", syncLang);
      window.clearInterval(timer);
    };
  }, []);

  const breadcrumbs = useMemo(() => {
    return [
      { label: ui.home, page: "home" },
      { label: "MYSTERY250", page: null },
    ];
  }, [ui.home]);

  function onGo(page) {
    const nav = typeof onNavigatePage === "function" ? onNavigatePage : onNavigate;
    if (!page) return;
    if (typeof nav === "function") nav(page);
  }

  const loadMysteries = useCallback(async (difficulty) => {
    try {
      setLoading(true);
      setError("");
      setSuccess("");

      const diff = String(difficulty || "ALL").toUpperCase();
      const qs = diff && diff !== "ALL" ? `?difficulty=${encodeURIComponent(diff)}` : "";

      const data = await api(`/api/mysteries${qs}`, { method: "GET" });

      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.mysteries)
        ? data.mysteries
        : Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data?.rows)
        ? data.rows
        : [];

      setMysteries(list);

      if (list.length > 0) {
        const requestedRef = readRequestedMysteryRef();
        const requestedMystery = findMysteryByRef(list, requestedRef);
        const requestedId = safeId(getId(requestedMystery));

        setActiveMysteryId((prev) => {
          if (requestedId) return requestedId;

          const prevId = safeId(prev);
          if (prevId) {
            const found = list.some((x) => safeId(getId(x)) === prevId);
            if (found) return prevId;
          }
          const firstId = safeId(getId(list[0]));
          return firstId || null;
        });
      } else {
        setActiveMysteryId(null);
      }
    } catch (err) {
      setError(err?.message || ui.failedLoad);
      setMysteries([]);
      setActiveMysteryId(null);
    } finally {
      setLoading(false);
    }
  }, [ui.failedLoad]);

  useEffect(() => {
    loadMysteries(filterDifficulty);
  }, [loadMysteries, filterDifficulty]);

  const activeMystery = useMemo(() => {
    const aid = safeId(activeMysteryId);
    if (!aid) return null;
    return mysteries.find((m) => safeId(getId(m)) === aid) || null;
  }, [mysteries, activeMysteryId]);

  const filteredMysteries = useMemo(() => {
    const q = safeStr(searchText).trim().toLowerCase();
    if (!q) return mysteries;

    return mysteries.filter((m) => {
      const haystack = [m?.title, m?.question, m?.difficulty, ...(Array.isArray(m?.options) ? m.options : [])]
        .map((x) => safeStr(x).toLowerCase())
        .join(" ");
      return haystack.includes(q);
    });
  }, [mysteries, searchText]);

  useEffect(() => {
    const aid = safeId(activeMysteryId);
    if (!aid || !listRef.current) return;

    const timer = window.setTimeout(() => {
      const safeSelector =
        typeof CSS !== "undefined" && typeof CSS.escape === "function"
          ? CSS.escape(aid)
          : aid.replace(/"/g, '\\"');
      const node = listRef.current?.querySelector?.(`[data-m250-id="${safeSelector}"]`);
      node?.scrollIntoView?.({ behavior: "smooth", block: "nearest", inline: "center" });
    }, 80);

    return () => window.clearTimeout(timer);
  }, [activeMysteryId, filteredMysteries.length]);

  const activeDifficulty = safeDifficulty(activeMystery?.difficulty || "EASY");
  const activeAuthorId = safeId(activeMystery?.authorId || "");
  const canEditActive = !!activeMystery && canCreate;

  const editingMystery = useMemo(() => {
    const eid = safeId(editingId);
    if (!eid) return null;
    return mysteries.find((m) => safeId(getId(m)) === eid) || null;
  }, [mysteries, editingId]);

  const editingAuthorId = safeId(editingMystery?.authorId || "");
  const canEditEditing = !!editingId && canCreate;

  const activeCoverUrl = useMemo(() => pickCoverUrl(activeMystery), [activeMystery]);
  const activeTrailer = useMemo(() => getTrailerEmbed(activeMystery?.videoUrl), [activeMystery]);
  const activeTrailerUrl = useMemo(() => sanitizeUrl(activeMystery?.videoUrl), [activeMystery]);

  const mysteryOfTheWeek = useMemo(() => {
    const rows = Array.isArray(mysteries) ? mysteries.slice() : [];
    if (!rows.length) return null;
    rows.sort((a, b) => mysteryTimeScore(b) - mysteryTimeScore(a));
    return rows[0] || null;
  }, [mysteries]);

  const mysteryOfTheWeekId = safeId(getId(mysteryOfTheWeek));
  const weeklyStats = useMemo(() => getMysteryStats(mysteryOfTheWeek), [mysteryOfTheWeek]);
  const activeStats = useMemo(() => getMysteryStats(activeMystery), [activeMystery]);
  const activeBody = useMemo(() => splitMysteryBody(activeMystery?.question || ""), [activeMystery]);
  const activeAttempt = activeMystery ? attemptLog[safeId(getId(activeMystery))] || readLocalAttempt(getId(activeMystery)) : null;
  const activeRevealOpen = isMysteryRevealOpen(activeMystery);
  const revealDateText = formatRevealDate(activeMystery?.revealAt);
  const activeHasAttempt = !!activeAttempt;
  const activeLearningNote = useMemo(
    () => guessLearningNote(activeMystery, ui.learningFallback),
    [activeMystery, ui.learningFallback]
  );



  useEffect(() => {
    if (!activeMystery) {
      resetPlay();
      return;
    }

    const attempt = activeAttempt;
    if (attempt?.picked) {
      setPicked(indexForLetter(attempt.picked));
      setSubmitted(true);
    } else {
      resetPlay();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMysteryId]);

  function clearFlash() {
    setError("");
    setSuccess("");
  }

  function handleFormChange(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function resetPlay() {
    setPicked(null);
    setSubmitted(false);
    setSubmittingAttempt(false);
  }

  function startCreate() {
    if (!canCreate) return;
    clearFlash();
    setMode("create");
    setEditingId(null);
    setForm(emptyForm());
    setSelectedImageFile(null);
    setSelectedVideoFile(null);
    requestAnimationFrame(() => {
      editorRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    });
  }

  function startEditFromActive() {
    if (!canEditActive || !activeMystery) return;
    clearFlash();

    const id = safeId(getId(activeMystery));
    if (!id) return;

    setMode("edit");
    setEditingId(id);

    const opts = splitOptionsToForm(activeMystery?.options || []);
    const correct = String(activeMystery?.correctOption || "A").toUpperCase();
    const diff = safeDifficulty(activeMystery?.difficulty);

    setForm({
      title: safeStr(activeMystery?.title || ""),
      question: safeStr(activeMystery?.question || ""),
      ...opts,
      correctOption: ["A", "B", "C", "D"].includes(correct) ? correct : "A",
      explanation: safeStr(activeMystery?.explanation || ""),
      difficulty: diff,
      imageUrl: safeStr(activeMystery?.imageUrl || ""),
      videoUrl: safeStr(activeMystery?.videoUrl || ""),
      revealAt: toDatetimeLocalValue(activeMystery?.revealAt || ""),
    });
    setSelectedImageFile(null);
    setSelectedVideoFile(null);

    requestAnimationFrame(() => {
      editorRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    });
  }

  function cancelEditOrCreate() {
    clearFlash();
    setMode("create");
    setEditingId(null);
    setForm(emptyForm());
    setSelectedImageFile(null);
    setSelectedVideoFile(null);
  }

  function mergeUpdatedMystery(updatedMystery) {
    const updatedId = safeId(getId(updatedMystery));
    if (!updatedId) return;

    setMysteries((prev) =>
      (Array.isArray(prev) ? prev : []).map((mystery) =>
        safeId(getId(mystery)) === updatedId ? { ...mystery, ...updatedMystery } : mystery
      )
    );
  }

  async function uploadMysteryImage() {
    if (!selectedImageFile || uploadingImage) return;
    clearFlash();
    try {
      setUploadingImage(true);
      const res = await api.uploadMysteryImage(selectedImageFile);
      const url = safeStr(res?.imageUrl || res?.url || "").trim();
      if (!url) throw new Error(ui.coverUploadFailed);
      setForm((prev) => ({ ...prev, imageUrl: url }));
      setSelectedImageFile(null);
      setSuccess(ui.coverUploadSuccess);
    } catch (err) {
      setError(err?.message || ui.coverUploadFailed);
    } finally {
      setUploadingImage(false);
    }
  }

  async function uploadMysteryVideo() {
    if (!selectedVideoFile || uploadingVideo) return;
    clearFlash();
    try {
      setUploadingVideo(true);
      const res = await api.uploadMysteryVideo(selectedVideoFile);
      const url = safeStr(res?.videoUrl || res?.url || "").trim();
      if (!url) throw new Error(ui.videoUploadFailed);
      setForm((prev) => ({ ...prev, videoUrl: url }));
      setSelectedVideoFile(null);
      setSuccess(ui.videoUploadSuccess);
    } catch (err) {
      setError(err?.message || ui.videoUploadFailed);
    } finally {
      setUploadingVideo(false);
    }
  }

  function validateForm() {
    const question = form.question.trim();
    if (!question) return ui.enterQuestion;

    const optionsRaw = [form.optionA, form.optionB, form.optionC, form.optionD].map((x) => safeStr(x).trim());
    const nonEmptyCount = optionsRaw.filter((x) => x).length;
    if (nonEmptyCount < 2) return ui.twoOptions;

    const correctLetter = String(form.correctOption || "A").toUpperCase();
    const correctIndex = indexForLetter(correctLetter);

    if (!optionsRaw[correctIndex]) {
      return ui.correctEmpty;
    }

    const v = String(form.videoUrl || "").trim();
    if (v && !sanitizeUrl(v)) {
      return ui.trailerMustHttp;
    }

    return null;
  }

  async function submitCreateOrEdit(e) {
    e.preventDefault();
    clearFlash();

    if (!canCreate) {
      setError(ui.loginCreateEdit);
      return;
    }

    if (mode === "edit") {
      if (!editingId) {
        setError(ui.noEditSelected);
        return;
      }
      if (!canEditEditing) {
        setError(ui.ownOnly);
        return;
      }
    }

    const v = validateForm();
    if (v) {
      setError(v);
      return;
    }

    const optionsFixed = [form.optionA, form.optionB, form.optionC, form.optionD].map((x) => safeStr(x).trim());

    const payload = {
      title: form.title.trim(),
      question: form.question.trim(),
      options: optionsFixed,
      correctOption: String(form.correctOption || "A").toUpperCase(),
      explanation: form.explanation.trim(),
      difficulty: safeDifficulty(form.difficulty),
      imageUrl: String(form.imageUrl || "").trim(),
      videoUrl: String(form.videoUrl || "").trim(),
      revealAt: revealAtPayload(form.revealAt),
    };

    try {
      setSaving(true);

      if (mode === "edit" && editingId) {
        await api(`/api/mysteries/${encodeURIComponent(editingId)}`, {
          method: "PUT",
          body: JSON.stringify(payload),
        });
        setSuccess(ui.changesSaved);
      } else {
        const res = await api("/api/mysteries", { method: "POST", body: JSON.stringify(payload) });
        const createdId = safeId(getId(res?.mystery));
        setSuccess(ui.mysteryCreated);

        const createdDifficulty = safeDifficulty(payload.difficulty);

        if (filterDifficulty !== "ALL" && String(filterDifficulty).toUpperCase() !== createdDifficulty) {
          setFilterDifficulty(createdDifficulty);
          if (createdId) setActiveMysteryId(createdId);
        } else {
          if (createdId) setActiveMysteryId(createdId);
          await loadMysteries(filterDifficulty);
        }

        cancelEditOrCreate();
        return;
      }

      await loadMysteries(filterDifficulty);
      cancelEditOrCreate();
    } catch (err) {
      setError(err?.message || (mode === "edit" ? ui.failedSaveChanges : ui.failedCreate));
    } finally {
      setSaving(false);
    }
  }

  async function submitPlay() {
    if (!activeMystery || picked == null || submittingAttempt || activeHasAttempt) return;

    const id = safeId(getId(activeMystery));
    const pickedLetter = letterForIndex(picked);
    const payload = {
      picked: pickedLetter,
      correct: "",
      ok: false,
      revealOpen: activeRevealOpen,
      revealAt: activeMystery?.revealAt || null,
      at: new Date().toISOString(),
    };

    try {
      setSubmittingAttempt(true);
      clearFlash();

      if (id) {
        const res = await api.submitMysteryAttempt(id, pickedLetter, {
          readerKey: readerKeyRef.current || "",
        });
        const attempt = res?.attempt && typeof res.attempt === "object" ? res.attempt : {};
        const serverPayload = {
          ...payload,
          picked: safeStr(attempt.picked || payload.picked),
          correct: safeStr(attempt.correct || ""),
          ok: attempt.ok === true,
          revealOpen: attempt.revealOpen === true,
          revealAt: attempt.revealAt || payload.revealAt,
          alreadyAttempted: res?.alreadyAttempted === true || attempt.alreadyAttempted === true,
          serverRecorded: true,
        };

        writeLocalAttempt(id, serverPayload);
        setAttemptLog((prev) => ({ ...prev, [id]: serverPayload }));

        if (res?.mystery) {
          mergeUpdatedMystery(res.mystery);
        }

        if (res?.alreadyAttempted === true) {
          setError(ui.alreadyAttempted);
        }
      }

      setSubmitted(true);
    } catch (err) {
      if (id) {
        const fallbackPayload = { ...payload, serverRecorded: false };
        writeLocalAttempt(id, fallbackPayload);
        setAttemptLog((prev) => ({ ...prev, [id]: fallbackPayload }));
      }

      setError(isAlreadyAttemptMessage(err?.message) ? ui.alreadyAttempted : err?.message || ui.attemptSyncFailed);
      setSubmitted(true);
    } finally {
      setSubmittingAttempt(false);
    }
  }

  function selectMystery(mysteryId, shouldScroll = false) {
    const id = safeId(mysteryId);
    if (!id) return;
    clearFlash();
    setActiveMysteryId(id);
    resetPlay();

    if (shouldScroll) {
      requestAnimationFrame(() => {
        listRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
      });
    }
  }

  const listCount = mysteries.length;
  const filteredCount = filteredMysteries.length;

  const optionsForActive = (Array.isArray(activeMystery?.options) ? activeMystery.options : []).slice(0, 4);
  const correctLetter = String(activeMystery?.correctOption || "").toUpperCase();
  const activeCanShowAnswer = activeRevealOpen && ["A", "B", "C", "D"].includes(correctLetter);
  const correctIndex = activeCanShowAnswer ? indexForLetter(correctLetter) : -1;

  const playResult = useMemo(() => {
    if (!submitted || picked == null || !activeCanShowAnswer || !correctLetter) return null;
    const ok = picked === correctIndex;
    return { ok, correct: correctLetter, picked: letterForIndex(picked) };
  }, [submitted, picked, correctIndex, correctLetter, activeCanShowAnswer]);

  return (
    <div className="ns-page m250">
      <div className="m250-hero-wrap">
        <div
          className="m250-hero ns-identity-hero"
          style={{
            backgroundImage:
              "linear-gradient(90deg, rgba(4, 31, 24, 0.94) 0%, rgba(4, 47, 34, 0.84) 48%, rgba(4, 31, 24, 0.68) 100%), url('/images/storyhub/storyhub-background.png')",
          }}
        >
          <div className="m250-hero-left ns-identity-hero-copy">
            <div className="m250-brand-mark">{ui.brand}</div>
            <div className="m250-kicker ns-identity-kicker">{ui.kicker}</div>
            <h1 className="m250-title ns-identity-title">{ui.title}</h1>
            <p className="m250-subtitle ns-identity-subtitle">{ui.subtitle}</p>

            <div className="m250-hero-meta ns-identity-actions">
              <span className="m250-chip">{listCount} {listCount === 1 ? ui.mysterySingular : ui.mysteryPlural}</span>
              <span className="m250-chip">{canCreate ? ui.toolsEnabled : ui.viewerMode}</span>

              <button
                type="button"
                className="ns-button ns-button-outline"
                onClick={() => loadMysteries(filterDifficulty)}
                disabled={loading}
              >
                {loading ? ui.refreshing : ui.refreshList}
              </button>

              {canCreate ? (
                <button type="button" className="ns-button ns-button-outline" onClick={startCreate}>
                  {ui.newMystery}
                </button>
              ) : (
                <button type="button" className="ns-button ns-button-outline" onClick={() => onGo("login")}>
                  {ui.loginToContribute}
                </button>
              )}
            </div>

            <div className="m250-breadcrumb-trail">
              {breadcrumbs.map((b, i) => {
                const isLast = i === breadcrumbs.length - 1;
                return (
                  <React.Fragment key={i}>
                    <button
                      type="button"
                      className={isLast ? "m250-bc-current" : "m250-bc-link"}
                      onClick={() => onGo(b.page)}
                      disabled={!b.page}
                    >
                      {b.label}
                    </button>
                    {!isLast ? <span className="m250-bc-sep">›</span> : null}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          <div className="m250-hero-right ns-identity-hero-stat">
            <div className="ns-identity-stat-number">{listCount}</div>
            <div className="ns-identity-stat-label">{listCount === 1 ? ui.mysterySingular : ui.mysteryPlural}</div>
            <div className="m250-hero-note">{ui.note}</div>
          </div>
        </div>
      </div>

      {mysteryOfTheWeek ? (
        <section className="ns-card m250-weekly-panel" aria-label={ui.weeklyKicker}>
          <div className="m250-weekly-media">
            <img
              src={pickCoverUrl(mysteryOfTheWeek)}
              alt={mysteryOfTheWeek?.title || ui.mysteryCover}
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          </div>

          <div className="m250-weekly-copy">
            <div className="m250-weekly-kicker">{ui.weeklyKicker}</div>
            <h2 className="m250-weekly-title">
              {clampStr(mysteryOfTheWeek?.title || ui.weeklyTitle, 120)}
            </h2>
            <p className="m250-weekly-text">{ui.weeklyPrompt}</p>

            <div className="m250-weekly-stats">
              <span className="m250-chip m250-chip-strong">{safeDifficulty(mysteryOfTheWeek?.difficulty)}</span>
              {weeklyStats.attempts != null ? (
                <span className="m250-chip">{weeklyStats.attempts} {attemptLabel(weeklyStats.attempts, ui)}</span>
              ) : (
                <span className="m250-chip">{ui.noAttemptsYet}</span>
              )}
              {weeklyStats.successRate != null ? (
                <span className="m250-chip">{weeklyStats.successRate}% {ui.successRate}</span>
              ) : null}
            </div>

            <button
              type="button"
              className="ns-button ns-button-primary m250-weekly-button"
              onClick={() => selectMystery(mysteryOfTheWeekId, true)}
            >
              {ui.tryMystery}
            </button>
          </div>
        </section>
      ) : null}

      <section className="ns-card m250-panel m250-filter-panel" aria-label={ui.filtersAria}>
        <div className="m250-filter-heading">
          <div>
            <div className="m250-panel-title">{ui.searchFilters}</div>
            <div className="m250-panel-sub">{ui.filterSub}</div>
          </div>
          <span className="m250-chip">{ui.showing} {filteredCount} {ui.of} {listCount}</span>
        </div>

        <div className="m250-filter-grid">
          <div className="m250-field">
            <label className="m250-label">{ui.search}</label>
            <input
              className="m250-input"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder={ui.searchPlaceholder}
            />
          </div>

          <div className="m250-field">
            <label className="m250-label">{ui.difficulty}</label>
            <select
              className="m250-select"
              value={filterDifficulty}
              onChange={(e) => {
                clearFlash();
                setFilterDifficulty(e.target.value);
              }}
              disabled={loading}
            >
              <option value="ALL">{ui.allDifficulties}</option>
              {DIFFICULTIES.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>

          <div className="m250-filter-actions">
            <button type="button" className="ns-button ns-button-outline" onClick={() => setSearchText("")} disabled={!searchText}>
              {ui.clear}
            </button>
          </div>
        </div>
      </section>

      <div className="m250-grid">
        <section className="ns-card m250-panel">
          <div className="m250-panel-head">
            <div>
              <div className="m250-panel-title">{ui.mysteries}</div>
              <div className="m250-panel-sub">{ui.pickOne}</div>
            </div>

            <div className="m250-list-controls">
              <button
                type="button"
                className="m250-shelf-arrow"
                aria-label={ui.scrollLeft}
                onClick={() => listRef.current?.scrollBy?.({ left: -360, behavior: "smooth" })}
              >
                ‹
              </button>
              <button
                type="button"
                className="m250-shelf-arrow"
                aria-label={ui.scrollRight}
                onClick={() => listRef.current?.scrollBy?.({ left: 360, behavior: "smooth" })}
              >
                ›
              </button>
              {canCreate ? (
                <button type="button" className="ns-button ns-button-primary" onClick={startCreate}>
                  {ui.newShort}
                </button>
              ) : null}
            </div>
          </div>


          {loading ? (
            <div className="m250-empty">{ui.loadingMysteries}</div>
          ) : mysteries.length === 0 ? (
            <div className="m250-empty">{ui.noMysteries}</div>
          ) : filteredMysteries.length === 0 ? (
            <div className="m250-empty">{ui.noMatch}</div>
          ) : (
            <div className="m250-list" ref={listRef}>
              {filteredMysteries.map((m, idx) => {
                const id = safeId(getId(m));
                const active = id && id === safeId(activeMysteryId);
                const diff = safeDifficulty(m?.difficulty);
                const cover = pickCoverUrl(m);
                const key = id || `row_${idx}`;

                return (
                  <button
                    key={key}
                    type="button"
                    data-m250-id={id}
                    className={"m250-list-item" + (active ? " m250-list-item-active" : "")}
                    onClick={() => selectMystery(id)}
                  >
                    {cover ? (
                      <div className="m250-list-cover" style={{ marginBottom: 8 }}>
                        <img
                          src={cover}
                          alt={m?.title || ui.mysteryCover}
                          style={{
                            width: "100%",
                            height: 120,
                            objectFit: "cover",
                            borderRadius: 10,
                            border: "1px solid #e5e7eb",
                            display: "block",
                          }}
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      </div>
                    ) : null}

                    <div className="m250-list-top">
                      <div className="m250-list-title">{clampStr(m?.title || ui.untitled, 64)}</div>
                      <div
                        className={
                          "m250-list-pill " +
                          (diff === "EASY"
                            ? "m250-pill-easy"
                            : diff === "MEDIUM"
                            ? "m250-pill-medium"
                            : diff === "HARD"
                            ? "m250-pill-hard"
                            : "m250-pill-expert")
                        }
                      >
                        {diff}
                      </div>
                    </div>
                    <div className="m250-list-sub">{clampStr(m?.question || "", 92)}</div>
                    <div className="m250-card-meta-row">
                      {safeId(getId(m)) === mysteryOfTheWeekId ? (
                        <span className="m250-mini-badge">{ui.weeklyKicker}</span>
                      ) : null}
                      {getMysteryStats(m).attempts != null ? (
                        <span className="m250-mini-badge">{getMysteryStats(m).attempts} {attemptLabel(getMysteryStats(m).attempts, ui)}</span>
                      ) : (
                        <span className="m250-mini-badge">{ui.notAttemptedByYou}</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </section>

        <div className="m250-right">
          <section className="ns-card m250-panel">
            <div className="m250-panel-head">
              <div>
                <div className="m250-panel-title">{ui.details}</div>
                <div className="m250-panel-sub">{activeMystery ? ui.readPuzzle : ui.selectMystery}</div>
              </div>

              {activeMystery ? (
                <div className="m250-detail-actions">
                  <span className="m250-chip">{activeDifficulty}</span>
                  {canEditActive ? (
                    <button type="button" className="ns-button ns-button-outline" onClick={startEditFromActive}>
                      {ui.edit}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>

            {!activeMystery ? (
              <div className="m250-empty">{ui.chooseMystery}</div>
            ) : (
              <div className="m250-details">
                {activeCoverUrl ? (
                  <div className="m250-detail-cover" style={{ marginBottom: 12 }}>
                    <img
                      src={activeCoverUrl}
                      alt={activeMystery?.title || ui.mysteryCover}
                      style={{
                        width: "100%",
                        height: "auto",
                        maxHeight: "none",
                        objectFit: "contain",
                        objectPosition: "center",
                        borderRadius: 18,
                        border: "1px solid rgba(15, 23, 42, 0.12)",
                        display: "block",
                        background: "#0f172a",
                      }}
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  </div>
                ) : null}

                {activeTrailer ? (
                  <div className="m250-detail-trailer">
                    <div className="m250-media-label">{ui.trailer}</div>

                    <div className="m250-trailer-frame">
                      {activeTrailer.type === "direct" ? (
                        <video
                          controls
                          src={activeTrailer.src}
                          style={{ width: "100%", display: "block", borderRadius: 12 }}
                        />
                      ) : (
                        <div style={{ position: "relative", paddingTop: "56.25%" }}>
                          <iframe
                            title={ui.trailer}
                            src={activeTrailer.embedSrc}
                            style={{
                              position: "absolute",
                              inset: 0,
                              width: "100%",
                              height: "100%",
                              border: 0,
                            }}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ) : null}

                {!activeTrailer && activeTrailerUrl ? (
                  <div className="m250-muted" style={{ marginBottom: 12 }}>
                    {ui.trailerLink}{" "}
                    <a href={activeTrailerUrl} target="_blank" rel="noreferrer">
                      {ui.openTrailer}
                    </a>
                  </div>
                ) : null}

                <div className="m250-details-title">{clampStr(activeMystery?.title || ui.untitled, 160)}</div>

                <div className="m250-pulse-panel">
                  <div className="m250-pulse-title">{ui.communityPulse}</div>
                  <div className="m250-pulse-grid">
                    <div className="m250-pulse-item">
                      <strong>{activeStats.attempts != null ? activeStats.attempts : "—"}</strong>
                      <span>{attemptLabel(activeStats.attempts, ui)}</span>
                    </div>
                    <div className="m250-pulse-item">
                      <strong>{activeStats.solved != null ? activeStats.solved : "—"}</strong>
                      <span>{ui.solved}</span>
                    </div>
                    <div className="m250-pulse-item">
                      <strong>{activeStats.successRate != null ? `${activeStats.successRate}%` : "—"}</strong>
                      <span>{ui.successRate}</span>
                    </div>
                  </div>
                  <div className="m250-pulse-note">
                    {activeAttempt ? ui.attemptedByYou : ui.notAttemptedByYou}
                    {activeStats.mostPicked ? ` · ${ui.mostReaders}: ${activeStats.mostPicked}` : ""}
                  </div>
                </div>

                <div className="m250-story-block">
                  <div className="m250-section-label">{ui.mysteryStory}</div>
                  <div className="m250-details-question">
                    {activeBody.story || safeStr(activeMystery?.question || "")}
                  </div>
                </div>

                <div className="m250-clue-block">
                  <div className="m250-section-label">{ui.clues}</div>
                  {activeBody.clues.length ? (
                    <div className="m250-clue-grid">
                      {activeBody.clues.map((clue, idx) => (
                        <div className="m250-clue-card" key={`${idx}_${clue}`}>
                          <span>{idx + 1}</span>
                          <p>{clue.replace(/^clue\s*\d+\s*:\s*/i, "")}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="m250-muted">{ui.clueFallback}</div>
                  )}
                </div>

                <div className="m250-question-callout">
                  <div className="m250-section-label">{ui.chooseAnswer}</div>
                  <strong>{activeBody.finalQuestion || ui.question}</strong>
                </div>

                <div className="m250-options">
                  {optionsForActive.map((opt, i) => {
                    const selected = picked === i;
                    let cls = "m250-option" + (selected ? " m250-option-selected" : "");
                    if (submitted && activeCanShowAnswer) {
                      if (i === correctIndex) cls += " m250-option-correct";
                      if (selected && i !== correctIndex) cls += " m250-option-wrong";
                    }
                    return (
                      <button
                        key={i}
                        type="button"
                        className={cls}
                        disabled={activeHasAttempt || submittingAttempt}
                        onClick={() => {
                          if (activeHasAttempt) return;
                          setPicked(i);
                          setSubmitted(false);
                        }}
                      >
                        <div className="m250-option-letter">{letterForIndex(i)}</div>
                        <div className="m250-option-text">{safeStr(opt)}</div>
                      </button>
                    );
                  })}
                </div>

                <div className="m250-submit-row">
                  <button type="button" className="ns-button ns-button-outline" onClick={resetPlay} disabled={(picked == null && !submitted) || submittingAttempt}>
                    {ui.reset}
                  </button>

                  <button type="button" className="ns-button ns-button-primary" disabled={picked == null || submittingAttempt || activeHasAttempt} onClick={submitPlay}>
                    {activeHasAttempt ? ui.submitLocked : submittingAttempt ? ui.recordingAttempt : ui.submitAnswer}
                  </button>
                </div>

                {submitted && playResult ? (
                  <div className={"m250-alert " + (playResult.ok ? "m250-alert-success" : "m250-alert-error")}>
                    {playResult.ok ? (
                      <div>
                        {ui.correct}
                        <div style={{ marginTop: 6, fontWeight: 800 }}>{ui.answer}: {playResult.correct}</div>
                      </div>
                    ) : (
                      <div>
                        {ui.notQuite}
                        <div style={{ marginTop: 6, fontWeight: 800 }}>
                          {ui.youPicked}: {playResult.picked} | {ui.answer}: {playResult.correct}
                        </div>
                      </div>
                    )}
                  </div>
                ) : null}

                {submitted ? <div className="m250-attempt-note">{activeCanShowAnswer ? ui.yourAttemptRecorded : ui.answerRecorded}</div> : null}

                {submitted && activeCanShowAnswer ? (
                  <div className="m250-reveal">
                    <div className="m250-reveal-row">
                      <span className="m250-reveal-label">{ui.reveal}</span>
                      <span className="m250-reveal-value">{correctLetter}</span>
                    </div>

                    {activeMystery?.explanation ? (
                      <div className="m250-reveal-expl">
                        <div className="m250-reveal-label">{ui.whyTrue}</div>
                        <div className="m250-reveal-text">{safeStr(activeMystery.explanation)}</div>
                      </div>
                    ) : (
                      <div className="m250-muted">{ui.noExplanation}</div>
                    )}

                    <div className="m250-learning-note">
                      <div className="m250-reveal-label">{ui.educativeNote}</div>
                      <p>{activeLearningNote}</p>
                    </div>
                  </div>
                ) : submitted ? (
                  <div className="m250-reveal m250-reveal-pending">
                    <div className="m250-reveal-label">{ui.revealPending}</div>
                    {revealDateText ? (
                      <p className="m250-muted">{ui.revealOpensOn}: {revealDateText}</p>
                    ) : null}
                  </div>
                ) : (
                  <div className="m250-muted">{ui.submitReveal}</div>
                )}
              </div>
            )}
          </section>

          <section className="ns-card m250-panel" ref={editorRef}>
            <div className="m250-panel-head">
              <div>
                <div className="m250-panel-title">{mode === "edit" ? ui.editMystery : ui.createMystery}</div>
                <div className="m250-panel-sub">{mode === "edit" ? ui.updateCarefully : ui.writeFair}</div>
              </div>

              {mode === "edit" ? (
                <button type="button" className="ns-button ns-button-outline" onClick={cancelEditOrCreate} disabled={saving}>
                  {ui.cancel}
                </button>
              ) : null}
            </div>

            {error ? <div className="m250-alert m250-alert-error">{error}</div> : null}
            {success ? <div className="m250-alert m250-alert-success">{success}</div> : null}

            {!canCreate ? (
              <div className="m250-empty">{ui.loginWriterAdmin}</div>
            ) : (
              <form onSubmit={submitCreateOrEdit} className="m250-form">
                <div className="m250-row">
                  <div className="m250-field" style={{ minWidth: 220 }}>
                    <label className="m250-label">{ui.difficulty}</label>
                    <select className="m250-select" value={form.difficulty} onChange={(e) => handleFormChange("difficulty", e.target.value)}>
                      {DIFFICULTIES.map((d) => (
                        <option key={d} value={d}>
                          {d}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="m250-field" style={{ flex: 1 }}>
                    <label className="m250-label">{ui.titleLabel}</label>
                    <input
                      className="m250-input"
                      value={form.title}
                      onChange={(e) => handleFormChange("title", e.target.value)}
                      placeholder={ui.titlePlaceholder}
                    />
                  </div>
                </div>

                <div className="m250-media-grid">
                  <div className="m250-field m250-upload-box">
                    <label className="m250-label">{ui.coverUploadLabel}</label>
                    <input
                      className="m250-file-input"
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={(e) => setSelectedImageFile(e.target.files?.[0] || null)}
                    />
                    {selectedImageFile ? (
                      <div className="m250-selected-file">
                        {ui.selectedFile}: {selectedImageFile.name}
                      </div>
                    ) : null}
                    <button
                      type="button"
                      className="ns-button ns-button-outline m250-upload-button"
                      onClick={uploadMysteryImage}
                      disabled={!selectedImageFile || uploadingImage || saving}
                    >
                      {uploadingImage ? ui.coverUploading : ui.coverUploadButton}
                    </button>
                    <div className="m250-footnote" style={{ marginTop: 8 }}>
                      {ui.coverUploadHint}
                    </div>
                  </div>

                  <div className="m250-field m250-upload-box">
                    <label className="m250-label">{ui.videoUploadLabel}</label>
                    <input
                      className="m250-file-input"
                      type="file"
                      accept="video/mp4,video/quicktime,video/webm,video/x-m4v"
                      onChange={(e) => setSelectedVideoFile(e.target.files?.[0] || null)}
                    />
                    {selectedVideoFile ? (
                      <div className="m250-selected-file">
                        {ui.selectedFile}: {selectedVideoFile.name}
                      </div>
                    ) : null}
                    <button
                      type="button"
                      className="ns-button ns-button-outline m250-upload-button"
                      onClick={uploadMysteryVideo}
                      disabled={!selectedVideoFile || uploadingVideo || saving}
                    >
                      {uploadingVideo ? ui.videoUploading : ui.videoUploadButton}
                    </button>
                    <div className="m250-footnote" style={{ marginTop: 8 }}>
                      {ui.videoUploadHint}
                    </div>
                  </div>
                </div>

                <div className="m250-field">
                  <label className="m250-label">{ui.coverUrl}</label>
                  <input
                    className="m250-input"
                    value={form.imageUrl}
                    onChange={(e) => handleFormChange("imageUrl", e.target.value)}
                    placeholder={ui.coverPlaceholder}
                  />
                  <div className="m250-footnote" style={{ marginTop: 8 }}>
                    {ui.coverTip}
                  </div>
                </div>

                <div className="m250-field">
                  <label className="m250-label">{ui.trailerUrl}</label>
                  <input
                    className="m250-input"
                    value={form.videoUrl}
                    onChange={(e) => handleFormChange("videoUrl", e.target.value)}
                    placeholder={ui.trailerPlaceholder}
                  />
                  <div className="m250-footnote" style={{ marginTop: 8 }}>
                    {ui.trailerTip}
                  </div>
                </div>

                <div className="m250-field">
                  <label className="m250-label">{ui.revealSchedule}</label>
                  <input
                    className="m250-input"
                    type="datetime-local"
                    value={form.revealAt}
                    onChange={(e) => handleFormChange("revealAt", e.target.value)}
                    placeholder={ui.revealSchedulePlaceholder}
                  />
                  <div className="m250-footnote" style={{ marginTop: 8 }}>
                    {ui.revealScheduleTip}
                  </div>
                </div>

                <div className="m250-field">
                  <label className="m250-label">{ui.question}</label>
                  <textarea
                    className="m250-textarea"
                    value={form.question}
                    onChange={(e) => handleFormChange("question", e.target.value)}
                    placeholder={ui.questionPlaceholder}
                    rows={5}
                  />
                </div>

                <div className="m250-options-grid">
                  <div className="m250-field">
                    <label className="m250-label">{ui.optionA}</label>
                    <input className="m250-input" value={form.optionA} onChange={(e) => handleFormChange("optionA", e.target.value)} />
                  </div>

                  <div className="m250-field">
                    <label className="m250-label">{ui.optionB}</label>
                    <input className="m250-input" value={form.optionB} onChange={(e) => handleFormChange("optionB", e.target.value)} />
                  </div>

                  <div className="m250-field">
                    <label className="m250-label">{ui.optionC}</label>
                    <input className="m250-input" value={form.optionC} onChange={(e) => handleFormChange("optionC", e.target.value)} />
                  </div>

                  <div className="m250-field">
                    <label className="m250-label">{ui.optionD}</label>
                    <input className="m250-input" value={form.optionD} onChange={(e) => handleFormChange("optionD", e.target.value)} />
                  </div>
                </div>

                <div className="m250-row">
                  <div className="m250-field" style={{ minWidth: 220 }}>
                    <label className="m250-label">{ui.correctOption}</label>
                    <select className="m250-select" value={form.correctOption} onChange={(e) => handleFormChange("correctOption", e.target.value)}>
                      <option value="A">A</option>
                      <option value="B">B</option>
                      <option value="C">C</option>
                      <option value="D">D</option>
                    </select>
                  </div>

                  <div className="m250-field" style={{ flex: 1 }}>
                    <label className="m250-label">{ui.explanationOptional}</label>
                    <input
                      className="m250-input"
                      value={form.explanation}
                      onChange={(e) => handleFormChange("explanation", e.target.value)}
                      placeholder={ui.explanationPlaceholder}
                    />
                  </div>
                </div>

                <div className="m250-submit-row">
                  {mode === "edit" ? (
                    <button type="button" className="ns-button ns-button-outline" onClick={cancelEditOrCreate} disabled={saving}>
                      {ui.cancel}
                    </button>
                  ) : null}

                  <button type="submit" className="ns-button ns-button-primary" disabled={saving}>
                    {saving ? ui.saving : mode === "edit" ? ui.saveChanges : ui.createMysteryButton}
                  </button>
                </div>

                <div className="m250-footnote">{ui.footnote}</div>
              </form>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
